import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { useHistory } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { PulseLoader } from "react-spinners";
import {
  GetAllDepartmentsForTelerikGrid,
  DeleteSelectedDepartments,
 } from "../../../../services/api/department-api";
import {
  GetAllSubDepartmentsForTelerikGrid,
  DeleteSubDepartments,
} from "../../../../services/api/subdepartment-api";
import { OrganizationMainHeader } from "./OrganizationMainHeader";
import { DepartmentGrid } from "../departments-new/DepartmentGrid";
import { SubDepartmentGrid } from "../subdepartments/SubDepartmentGrid";
import { successNotification, errorNotification } from "../../../../redux/notifications";
import "./organization.scss";

const OrganizationMain = ({ currentSubTab = "Departments", permission, currentUser }) => {
  const [departmentData, setDepartmentData] = useState({
    dataArray: [],
    data: null,
    isLoading: true,
  });
  const [subdepartmentData, setSubdepartmentData] = useState({
    dataArray: [],
    data: null,
    isLoading: true,
  });
  const [type, setType] = useState(currentSubTab);
  const [subDepartmentRolePrivilege, setSubDepartmentRolePrivilege] = useState(true);
  const [departmentRolePrivilege, setDepartmentRolePrivilege] = useState(true);
  const dispatch = useDispatch();

  let filters =
    {
      userId: 0,
      sortBy: "Name",
      sortDesc: false,
      pageNumber: 1,
      pageSize: 10000000,
      filters: [],
    };

  let history = useHistory();

  const GetDepartmentsData = async () => {
    try {
      setDepartmentData((prev) => ({
        ...prev,
        isLoading: true,
      }));

      let result = await GetAllDepartmentsForTelerikGrid(filters);

      if (result?.response?.status === 500) {
        setDepartmentRolePrivilege(false);
        setDepartmentData((prev) => ({ ...prev, 
          data: {},
          dataJSON: [],
          allColumns: [],
          isLoading: false, }));
        return;
      }
      setDepartmentData((prev) => ({
        ...prev,
        data: result?.data?.data,
        dataJSON: result?.data?.dataJSON,
        allColumns: result?.data?.allColumns,
        isLoading: false,
      }));
    } catch (e) {
      dispatch(
        errorNotification({
          header: "Error",
          message: "Failed to fetch Departments data.",
        })
      );
    }
  };

  const GetSubDepartmentsData = async () => {
    try {
      setSubdepartmentData((prev) => ({
        ...prev,
        isLoading: true,
      }));

      let result = await GetAllSubDepartmentsForTelerikGrid(filters);
      if (result?.response?.status === 500) {
        setSubDepartmentRolePrivilege(false);
        setSubdepartmentData((prev) => ({ ...prev, 
          data: {},
          dataJSON: [],
          allColumns: [],
          isLoading: false, }));
        return;
      }
      setSubdepartmentData((prev) => ({
        ...prev,
        data: result?.data?.data,
        dataJSON: result?.data?.dataJSON,
        allColumns: result?.data?.allColumns,
        isLoading: false,
      }));
    } catch (e) {
      console.log("Error [GetSubDepartments] => ", e);
      dispatch(
        errorNotification({
          header: "Error",
          message: "Failed to fetch Sub-Departments data.",
        })
      );
    }
  };

  const Archive = async (ids, type) => {
    try {
      const response = type === "SubDepartment" || type === "Sub-Department"
        ? await DeleteSubDepartments(ids)
        : await DeleteSelectedDepartments(ids);

      const getResponse = type === "SubDepartment" || type === "Sub-Department"
        ? await GetSubDepartmentsData()
        : await GetDepartmentsData();

      if (response.status === 200) {
        const successArchive = response?.data?.filter(x => x.Success) || [];
        const failArchive = response?.data?.filter(x => !x.Success) || [];

        if (successArchive?.length > 0) {
          const xNames = successArchive.map(x => x.Name).join(", ");
          const pluralCheck = successArchive.length > 1 ? "s" : "";

          dispatch(
            successNotification({
              header: `${type}${pluralCheck} Archived`,
              message: `${type}${pluralCheck} ${xNames} - successfully archived! Contact support@triyo.io to unarchive`,
            })
          );
        }

        if (failArchive?.length > 0) {
          const xNames = failArchive.map(x => x.Name).join(", ");
          const pluralCheck = failArchive.length > 1 ? "s" : "";

          dispatch(
            errorNotification({
              header: `${type}${pluralCheck} not Archived`,
              message: `${type}${pluralCheck} ${xNames} - could not be archived because it contains linked users`,
            })
          );
        }
      } else {
        dispatch(
          successNotification({
            header: `${type}(s) Archived`,
            message: `${type}(s) successfully archived! Contact support@triyo.io to unarchive`,
          })
        );
      }
    } catch (e) {
      console.log(`Error [${type ?? ""}] => `, e);

      dispatch(
        errorNotification({
          header: "Error",
          message: `Something went wrong. Please try again!`,
        })
      );
    }
  }

  useEffect(() => {
    if (currentSubTab === "") {
      if (history?.location?.search?.toLowerCase()?.includes("subdepartments")) {
        setType("SubDepartments");
      } else {
        setType("Departments");
      }
    }
    GetDepartmentsData();
    GetSubDepartmentsData();
  }, []);

  return (
    <div className="organization-main">
      <Helmet>
        <title> TRIYO · Organization </title>
      </Helmet>
      <OrganizationMainHeader
        departmentData={departmentData}
        subdepartmentData={subdepartmentData}
        type={type}
        setType={setType}
      />
      { type === "Departments" && departmentData.isLoading ?
        <div className="pulse-loader" data-testid="loading-spinner">
          <PulseLoader/>
        </div>
      :
        type === "Departments" &&
        <div className="organization-tab-grid" data-testid="department-grid">
          <DepartmentGrid
            data={departmentData}
            fetchEndpoint={Archive}
            type={type}
            departmentRolePrivilege={departmentRolePrivilege}
          />
        </div>
      }
      { type === "SubDepartments" && subdepartmentData.isLoading ?
        <div className="pulse-loader" data-testid="loading-spinner">
          <PulseLoader/>
        </div>
      :
        type === "SubDepartments" &&
          <div className="organization-tab-grid" data-testid="subdepartment-grid">
            <SubDepartmentGrid
              data={subdepartmentData}
              fetchEndpoint={Archive}
              type={type}
              subDepartmentRolePrivilege={subDepartmentRolePrivilege}
            />
          </div>
      }
    </div>
  );
};

OrganizationMain.propTypes = {
  currentSubTab: PropTypes.string,
  permission: PropTypes.object,
  currentUser: PropTypes.object,
};

export default OrganizationMain;
